import React, {useCallback, useContext, useEffect, useMemo, useRef, useState}     from "react"
import {useLocation, useNavigate, useParams}                                      from 'react-router-dom'
import {DEBUG, logger}                                                            from "../../util/log-utils"
import {doPost, doPut, doStreamPost}                                              from "../../util/do-fetch"
import {
	Avatar,
	Box,
	Button,
	Container,
	Divider,
	Fab,
	IconButton,
	Slide,
	Zoom,
	Typography,
	useMediaQuery,
	useTheme
} from "@mui/material"
import "../../stylesheets/PrepKit.css"
import "../../stylesheets/QuestionChat.css"
import Grid                                                             from "@mui/material/Unstable_Grid2"
import {
	abcd,
	createQueuedUserMessage,
	createStreamingAiMessage,
	dcba,
	getQuestionActions,
	hasStreamingMessage,
	rememberLastQuestion,
} from "../../util/prepkit-utils"
import SendMessage                                                      from "../prepkit/chat/SendMessage.js"
import Messages                                                                   from "../prepkit/chat/Messages.js"
import {useUserStore}                                                                        from "../../state"
import {
	ArrowBack,
	ArrowBackOutlined,
	ArrowForwardOutlined, Check, Close, Home,
	HomeOutlined,
	KeyboardArrowUp,
	KeyboardArrowRight,
	KeyboardDoubleArrowRight,
	KeyboardArrowLeft,
	KeyboardBackspace
} from "@mui/icons-material"
import {useInView}                             from "react-intersection-observer"
import Question                                from "./Question"
import {PrepKitContext, PrepKitRefreshContext} from "./PrepKit"
import {findQuestionInPrepKit, findTopicInPrepKit} from "../../util/mini"
import moment              from "moment"
import Score               from "./Score"
import StreamingMarkdown
                           from "../prepkit/chat/StreamingMarkdown"
import {keyframes}         from "@emotion/react"
import QuestionActions from "./QuestionActions"
import QuestionInput   from "./QuestionInput"
import AskBirdsyPanel  from "./AskBirdsyPanel"
import QuickStartActions from "./QuickStartActions"
import TopicScore          from "./TopicScore"
import TopicTitle          from "./TopicTitle"
import LeftRightCenter     from "../LeftRightCenter"
import {useSwipeable}      from "react-swipeable"
import Tour                from "./Tour"
import {Helmet}            from "react-helmet-async"

const log = logger("QuestionView", DEBUG)

const bw = 0

/*
 * Content caps for the two panes. They are in the same 7:5 proportion as the columns, so both
 * sides reach their cap at the same window width and stop growing together (a flat cap on both
 * let the narrower chat column keep expanding long after the question column had stopped).
 */
// the mockup's Submit-Answer pulse (A_Questions_v2.html: @keyframes softPulse)
const softPulse = keyframes`
	0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.5); }
	50%      { box-shadow: 0 0 0 8px rgba(99, 102, 241, 0); }
`

const QUESTION_CONTENT_MAX = 860
const CHAT_CONTENT_MAX = Math.round(QUESTION_CONTENT_MAX * 5 / 7)   // 614


function QuestionView() {
	log.trace("Component render")
	const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('md'))
	const theme = useTheme()
	const navigate = useNavigate()
	const location = useLocation()
	const {user} = useUserStore()
	const {code} = useParams()
	const prepKit = useContext(PrepKitContext)
	const refreshPrepKit = useContext(PrepKitRefreshContext)
	const hash = location.hash

	const [loadedAt, setLoadedAt] = useState()
	const [messages, setMessages] = useState([])
	const [isAutoScroll, setIsAutoScroll] = useState(true)

	/*
	 * Answers given during THIS visit, keyed by question id (101).
	 *
	 * `question.userAnswer` is the session's answer as the server last sent it, and prepKit is only
	 * refreshed by the realtime `answer` subscription — so an answer given a moment ago may not be
	 * in it yet. That lag is what 099 was patching around with pendingReviewRef. Overlaying this
	 * map removes the race rather than waiting it out: the moment the last question is answered,
	 * this component knows the topic is complete.
	 */
	const [answersThisVisit, setAnswersThisVisit] = useState({})

	/*
	 * Questions the student has unlocked with Change Answer. The session's answer is still on
	 * record; this only makes the options clickable again so a replacement can be given.
	 */
	const [unlockedQuestionIds, setUnlockedQuestionIds] = useState([])

	// set as soon as a wrong answer is clicked, so the correct option is shown alongside it
	const [revealCorrect, setRevealCorrect] = useState(false)
	// Quick-Start actions already used on the current question — cleared when the question changes
	const [usedActionTypes, setUsedActionTypes] = useState([])

	const [showTopic, setShowTopic] = useState(false)
	/*
	 * The id of the topic whose summary is on screen, or false.
	 *
	 * An ID rather than the topic object (101): showSummaryFor refreshes the prepkit so the score
	 * tiles reflect the answer that just completed the session, and a held object reference would
	 * still be the pre-refresh copy. Resolving from prepKit on every render means the tiles settle
	 * as the refresh lands.
	 */
	const [showTopicScore, setShowTopicScore] = useState(false)
	const [topicScoreMessage, setTopicScoreMessage] = useState(null)

	/* drives the mobile "back to question" FAB — inView is false only once the whole block is off-screen */
	const {ref: questionBlockRef, inView: questionInView} = useInView({threshold: 0})

	const messagesEndRef = useRef(null)
	const lastScrollTopRef = useRef(0)
	const prevTopicIdRef = useRef()
	const prevQuestionRef = useRef()
	/*
	 * The topic whose session THIS VISIT'S answer just completed (101).
	 *
	 * This is what makes the summary fire on a transition rather than on a state. Pressing Next in
	 * a finished topic they came back to Review must move to the next question; pressing Next after
	 * answering its last outstanding question must show the summary. Only the transition tells
	 * those two apart, and only this component sees it.
	 *
	 * It replaces 099's pendingReviewRef, and the race that made it necessary: the completion is
	 * known here at the moment of the click, so there is nothing left to wait for.
	 */
	const justCompletedTopicRef = useRef(null)
	/*
	 * The question currently on screen. Chat requests are not cancellable (doStreamFetch has no
	 * AbortController), so a response can arrive after the user has navigated away. The stream
	 * callbacks compare against this to discard anything that arrives for a question we left.
	 */
	const questionIdRef = useRef()

	const scrollToBottom = (element = messagesEndRef?.current, animate = true) => {
		if (element) {
			element.scrollIntoView({ behavior: animate ? 'smooth' : 'auto' })
		}
	}

	const handleSwipe = (direction) => {
		if (direction === 'left') {
			showTopic
				? handleNext()
				: nextQuestion()
		}
		else if (direction === 'right') {
			showTopic
				? handleBack()
				: previousQuestion()
		}
	}

	const handlers = useSwipeable({
		onSwipedLeft: () => handleSwipe('left'),
		onSwipedRight: () => handleSwipe('right')
	})

	const isBack = useMemo(() => {
		return hash === '#back'
	}, [hash])

	const {topic, question} = useMemo(() => {
		if (prepKit && code) {
			return findQuestionInPrepKit(prepKit, code)
		}
		return {product: null, topic: null, question: null}
	}, [prepKit, code])

	const parentTopics = useMemo(() => {
		if (topic) {
			return topic.path.split('/')
				.filter(p => p)
				.slice(0, -1)
		}
		else {
			return null
		}
	}, [topic])

	// the conversation for this visit — older messages are not loaded (see 057)
	const filteredMessages = useMemo(() => {
		return messages && messages.filter(m => moment(m.created_at)
			.isSameOrAfter(loadedAt || 0))
	}, [loadedAt, messages])

	/*
	 * The answer for a question IN THE CURRENT SESSION (101): what this visit recorded, else what
	 * the server sent. `question.userAnswer` is itself session-scoped now, so an answer from an
	 * archived attempt never appears here.
	 */
	const answerForQuestion = useCallback((q) => {
		if (!q) return null
		return answersThisVisit[q.id] || q.userAnswer || null
	}, [answersThisVisit])

	/*
	 * The answer on screen.
	 *
	 * This deliberately reverses 057 for the current session: a session answer now pre-fills its
	 * question, because "as users navigate through the topic, they see the answer for that
	 * session" is the point of having sessions. What 057 stopped — an answer from a previous
	 * attempt pre-filling a fresh one — is prevented upstream instead, by userAnswer being scoped
	 * to the live session.
	 *
	 * An unlocked question shows no answer: Change Answer clears the board so the next click is an
	 * answer rather than a correction of a still-highlighted one.
	 */
	const unlocked = Boolean(question && unlockedQuestionIds.includes(question.id))
	const answerToShow = unlocked ? null : answerForQuestion(question)

	// drives the Skip / Next button
	const answered = Boolean(answerToShow)

	/*
	 * Is every question in this topic answered in the current session?
	 *
	 * Read from this component's own view rather than topic.percentAnswered, so it is true the
	 * instant the last answer is given instead of one realtime round trip later.
	 */
	const topicComplete = useMemo(() => {
		const questions = (topic && topic.questions) || []
		if (!questions.length) return false
		return questions.every(q => Boolean(answerForQuestion(q)))
	}, [topic, answerForQuestion])

	/*
	 * Record an answer for this visit, and arm the summary if it was the one the topic was waiting
	 * on.
	 *
	 * Completion is computed here against the answer being recorded, not read from `topicComplete`
	 * — that memo still holds the value from before this answer, and would be one question behind
	 * on exactly the click that matters.
	 *
	 * Arming is only a UI intent. The server decides whether the SESSION is complete, in the answer
	 * route; this decides whether to show the student a summary.
	 */
	const recordAnswer = (q, answer) => {
		setAnswersThisVisit(prev => {
			const next = {...prev, [q.id]: answer}

			const questions = (topic && topic.questions) || []
			const nowComplete = questions.length > 0 &&
				questions.every(x => Boolean(next[x.id] || x.userAnswer))

			if (nowComplete && topic) justCompletedTopicRef.current = topic.id

			return next
		})
	}

	// the scored topic behind the summary screen, resolved fresh from prepKit on every render
	const topicScoreTopic = useMemo(() => {
		if (!showTopicScore || !prepKit) return null
		return findTopicInPrepKit(prepKit, showTopicScore) || null
	}, [showTopicScore, prepKit])

	/*
	 * text.muted reads as disabled against the dark panes, so chrome labels (nav buttons, topic,
	 * counter) step up to chip.text there. Light mode keeps text.muted.
	 */
	const chromeText = theme.palette.mode === 'dark' ? theme.custom.chip.text : theme.custom.text.muted

	// the Quick-Start buttons swap on answer state (see A_Questions_v2 mockup), minus any the
	// user has already asked for on this question
	const questionActions = useMemo(() => {
		return getQuestionActions({
			answered: Boolean(answerToShow),
			isCorrect: Boolean(answerToShow && answerToShow.is_correct),
		}).filter(a => !usedActionTypes.includes(a.type))
	}, [answerToShow, usedActionTypes])

	// window scroll listener
	useEffect(() => {
		window.addEventListener('scroll', handleScroll)
		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	// auto scroll to bottom
	useEffect(() => {
		if (isAutoScroll) {
			scrollToBottom()
		}
	}, [messages, isAutoScroll])

	// reset local state when question changes
	useEffect(() => {
		const prevQuestion = prevQuestionRef.current
		const questionChanged =
			(prevQuestion == null && question != null) ||
			(prevQuestion != null && question == null) ||
			(prevQuestion != null &&
				question != null &&
				prevQuestion.id !== question.id)

		if (questionChanged) {
			/*
			 * NOTE: this used to clear `currentAnswer` here, which is what made a previously
			 * answered question come up blank. Answers now live in `answersThisVisit`, keyed by
			 * question id, and are deliberately NOT cleared: navigating back to a question inside
			 * the same session must show the answer given for it (101).
			 */
			/*
			 * Each question gets its own conversation (098).
			 *
			 * This state is what handleSend posts to the API, unfiltered. Bumping loadedAt hides
			 * older messages from the SCREEN via filteredMessages, but the send path reads
			 * `messages` directly -- so without this the model received every turn from every
			 * question visited this session, and was asked to stay on topic while reading a
			 * transcript about three other ones.
			 */
			setMessages([])
			setLoadedAt(moment())
			setRevealCorrect(false)
			setUsedActionTypes([])
			// so "Continue Studying" on the dashboard comes back here
			rememberLastQuestion(prepKit && prepKit.id, question && question.code)
		}

		// Update the refs with the current question
		prevQuestionRef.current = question
		questionIdRef.current = question ? question.id : null

	}, [question])

	/*
	 * Streams the AI summary for a completed session.
	 *
	 * Only `topic.id` is read server-side (101) — the API rebuilds the topic, its questions and
	 * the session's answers from the database, so the summary can never be built from a stale
	 * client copy of the very answer that completed it.
	 */
	const streamTopicReview = useCallback((topicToReview) => {
		const aiMsg = createStreamingAiMessage(true, false)
		aiMsg.stream = true

		setTopicScoreMessage(aiMsg)

		const onData = (data) => {
			setTopicScoreMessage(prev => {
				const clone = {...prev}
				clone.streaming = true
				clone.body += data
				return clone
			})
		}

		const onComplete = (data) => {
			setTopicScoreMessage(prev => {
				const clone = {...prev}
				clone.streaming = false
				clone.body = data
				return clone
			})
		}

		const url = `/mini/prepkit/${prepKit.id}/review-topic`
		const options = {
			body: JSON.stringify({topic: {id: topicToReview.id}})
		}
		// onResponse was unused here; it now carries the debug capture id (097)
		const onResponse = (response) => {
			const debugCaptureId = response.headers.get('X-Debug-Capture-Id')
			if (!debugCaptureId) return
			setTopicScoreMessage(prev => (prev ? {...prev, debug_capture_id: debugCaptureId} : prev))
		}

		doStreamPost(url, options, onResponse, onData, onComplete)
			.catch(err => {
				log.debug("Error streaming topic review")
				log.debug(err)
				/*
				 * The summary panel is already on screen with a spinner, so the failure has to land
				 * in it or it spins forever. Same treatment the chat bubble gets.
				 */
				setTopicScoreMessage(prev => (prev ? {
					...prev,
					body: err.message || "I couldn't put your summary together just now.",
					streaming: false,
					error: true,
				} : prev))
			})
	}, [prepKit])

	/*
	 * Show the Topic Summary for a topic whose session has just completed.
	 */
	const showSummaryFor = useCallback((topicToReview) => {
		justCompletedTopicRef.current = null
		setShowTopic(true)
		setShowTopicScore(topicToReview.id)
		streamTopicReview(topicToReview)
		// completed_at moved server-side when the last answer landed; pull it so the dashboard and
		// the question screen agree about the session's state
		refreshPrepKit && refreshPrepKit().catch(err => log.debug(err))
	}, [streamTopicReview, refreshPrepKit])

	/*
	 * The topic-title interstitial on a topic change.
	 *
	 * The summary trigger used to live here too, and that was the whole difficulty (099): it got
	 * exactly one chance, at the instant the topic changed, and depended on data that arrived over
	 * a realtime subscription. It is now driven by Next instead — see handleNext — where the fact
	 * that matters (did this visit's answer complete the topic?) is known for certain.
	 */
	useEffect(() => {
		if (!topic) return

		if (prevTopicIdRef.current && prevTopicIdRef.current !== topic.id) {
			setShowTopic(true)
			setShowTopicScore(false)
		}

		prevTopicIdRef.current = topic.id
	}, [topic])

	/*
	 * Where "back" goes, and what it is called (087). Defaults to the prepkit dashboard — where
	 * the link has always gone — but a screen that links into a question can name its own way
	 * back by passing state:
	 *
	 *   navigate(url, {state: {from: {label: 'Progress Report', path: `/prepkit/${id}/progress`}}})
	 *
	 * Label and path travel together so the link can never say one thing and do another.
	 */
	const backDestination = useMemo(() => {
		const from = location.state && location.state.from
		if (from && from.label && from.path) return from
		return {label: 'Dashboard', path: `/prepkit/${prepKit && prepKit.id}`}
	}, [location.state, prepKit])

	// carry the origin forward, or moving one question on would silently reset the label
	const nextQuestion = async () => {
		navigate(`/prepkit/${prepKit.id}/q/${question.next}#next`, {state: location.state})
	}

	const previousQuestion = async () => {
		navigate(`/prepkit/${prepKit.id}/q/${question.previous}#back`, {state: location.state})
	}

	const saveAnswer = async (answer) => {
		const url = `/mini/prepkit/${prepKit.id}/question/${question.id}/answer/${answer}`
		await doPut(url)
	}

	const handleScroll = () => {
		const scrollTop = window.scrollY
		const scrollHeight = document.documentElement.scrollHeight
		const clientHeight = window.innerHeight

		// Determine scroll direction
		const scrollDirection = scrollTop > lastScrollTopRef.current ? 'down' : 'up'
		lastScrollTopRef.current = scrollTop

		// If the user scrolls up, disable auto-scroll
		if (scrollDirection === 'up') {
			setIsAutoScroll(false)
		}

		// Re-enable auto-scroll if the user is at the bottom of the page
		if (scrollHeight - scrollTop === clientHeight) {
			setIsAutoScroll(true)
		}
	}

	const handleBack = () => {
		if (showTopicScore) {
			/*
			 * showTopic has to come down with it. showSummaryFor raises both (the overlay only
			 * renders while showTopic is true), so clearing the score alone would leave the topic
			 * TITLE screen standing where the question should be.
			 */
			setShowTopicScore(false)
			setShowTopic(false)
		}
		else {
			if (showTopic) {
				setShowTopic(false)
				if (!isBack) previousQuestion()
			}
			else {
				previousQuestion()
			}
		}
	}

	/*
	 * Next.
	 *
	 * The Topic Summary is reached from here (101), and only on a transition: the student answered
	 * the last outstanding question of the topic during this visit, and then pressed Next. Where
	 * that question sat in the topic does not matter — answering question 2 last completes the
	 * session exactly as answering question 10 last does.
	 *
	 * Change Answer re-arms it: replacing an incorrect answer completes the session again, so the
	 * next Next lands on the summary again.
	 *
	 * Re-reading a finished topic does NOT trigger it, because no transition happened. That is what
	 * makes Review navigable.
	 */
	const handleNext = () => {
		if (showTopicScore) {
			setShowTopicScore(false)
			// see handleBack: the overlay is raised by showTopic, so it comes down too. The
			// topic-change effect raises it again if the navigation below lands in a new topic.
			setShowTopic(false)

			/*
			 * On from the summary is the next TOPIC, not question.next.
			 *
			 * The session can be completed from anywhere in the topic — answering question 2 last
			 * finishes it — so question.next would drop the student back into the middle of a topic
			 * they have just been told they finished. The question after the topic's last one is
			 * the first of the next topic, which is where "Continue" means to go.
			 */
			const questions = (topicScoreTopic && topicScoreTopic.questions) || []
			const lastInTopic = questions[questions.length - 1]
			if (lastInTopic && lastInTopic.next) {
				navigate(`/prepkit/${prepKit.id}/q/${lastInTopic.next}#next`, {state: location.state})
			}
			else {
				nextQuestion()
			}
			return
		}

		if (showTopic) {
			setShowTopic(false)
			if (isBack) nextQuestion()
			return
		}

		if (topic && justCompletedTopicRef.current === topic.id && topicComplete) {
			showSummaryFor(topic)
			return
		}

		nextQuestion()
	}

	/*
	 * Question variants are no longer reachable from the UI (the "Ask a different way" button
	 * was removed in 037), but the endpoint and the QUESTION_VARIANT renderer stay so variant
	 * messages already in history still work.
	 */
	const triggerVariantFlow = async () => {
		const loadingMsg = {
			id: 'variant-loading',
			author_type: 'AI',
			message_type: 'TEXT',
			message_sub_type: 'QUESTION_VARIANT_LOADING',
			body: '',
			created_at: new Date()
		}
		setMessages(prev => [...prev, loadingMsg])
		setTimeout(() => scrollToBottom(), 100)
		try {
			const response = await doPost(
				`/mini/prepkit/${prepKit.id}/question/${question.id}/variant`
			)
			if (response.ok) {
				const variantMessage = await response.json()
				setMessages(prev =>
					prev.map(m => m.id === 'variant-loading' ? variantMessage : m)
				)
				setTimeout(() => scrollToBottom(), 100)
			} else {
				setMessages(prev => prev.filter(m => m.id !== 'variant-loading'))
			}
		} catch (err) {
			console.error('Error generating variant', err)
			setMessages(prev => prev.filter(m => m.id !== 'variant-loading'))
		}
	}

	/*
	 * answeredLetter: the option letter when this send came from CLICKING an answer, otherwise
	 * undefined (082). The server records nothing from typed chat — it used to run an AI
	 * classifier over the message text to guess, which recorded "is it B?" as answering B.
	 * saveAnswer() below remains the only thing that writes an answer.
	 */
	const handleSend = async (message, answeredLetter) => {
		setShowTopic(false)

		// Handle variant action objects from any caller (popup menu, etc.)
		if (message && typeof message === 'object' && message.variant) {
			triggerVariantFlow()
			return
		}

		const msgs = [...messages]

		/*
		 * Quick-Start actions arrive as {displayText, type}. The instruction text is not held
		 * here — the API resolves it from the `config` row for that type (062) — so only the
		 * type travels with the message.
		 */
		let displayText = message
		let actionType = null
		if (message && typeof message === 'object' && message.displayText) {
			displayText = message.displayText
			actionType = message.type || null

			// a Quick-Start action only gets one use per question
			if (actionType) {
				setUsedActionTypes(prev => prev.includes(actionType) ? prev : [...prev, actionType])
			}
		}

		if (!displayText || displayText.trim().length === 0) {
			return
		}

		let userMsg = createQueuedUserMessage(displayText, true, false)
		msgs.push(userMsg)

		// for the server: same text, plus which action it came from (if any)
		const serverMsgs = [...msgs]
		if (actionType) {
			serverMsgs[serverMsgs.length - 1] = {
				...userMsg,
				body: displayText,
				displayBody: displayText,
				actionType: actionType,
			}
		}

		const aiMsg = createStreamingAiMessage(true, false)
		aiMsg.streaming = true

		/*
		 * Which question this send belongs to. The request cannot be cancelled, so if the user
		 * navigates before it resolves the callbacks below still fire — applying the old
		 * question's answer to whatever is now on screen (a selected option appearing without a
		 * click, and no visible reply because the streamed message predates the new loadedAt).
		 */
		const sentForQuestionId = question ? question.id : null
		const isStale = () => questionIdRef.current !== sentForQuestionId

		const handleSendResponse = async (response) => {
			if (isStale()) return

			const answerStatus = response.headers.get('X-Answer-Status') || 'NONE'
			const answerChoice = response.headers.get('X-Answer-Choice') || null
			// present only in admin debug mode (097); keys the "sent to the model" drawer
			const debugCaptureId = response.headers.get('X-Debug-Capture-Id')

			if (debugCaptureId) {
				setMessages(prev => {
					const index = prev.findIndex(m => m.id === aiMsg.id)
					if (index === -1) return prev
					const updated = [...prev]
					updated[index] = {...updated[index], debug_capture_id: debugCaptureId}
					return updated
				})
			}

			// apply message_sub_type to the streaming AI message
			if (answerStatus === 'CORRECT' || answerStatus === 'INCORRECT') {
				setMessages(prev => {
					const index = prev.findIndex(m => m.id === aiMsg.id)
					if (index === -1) return prev
					const updatedMessages = [...prev]
					updatedMessages[index] = {
						...updatedMessages[index],
						message_sub_type: answerStatus
					}
					return updatedMessages
				})
			}

			if(answerChoice) {
				const answer = dcba(answerChoice)

				recordAnswer(question, {
					question: question,
					answer: answer,
					is_correct: question.answer === answer,
					created_at: new Date()
				})
			}
		}

		// add aiMsg to local state but NOT to array sent to server
		log.debug("setMessages:: adding aiMsg")
		setMessages([...msgs, aiMsg])

		setTimeout(() => {
			setIsAutoScroll(true)
			scrollToBottom()
		}, 100)

		const onData = (data) => {
			if (isStale()) return
			log.debug("setMessages:: streaming onData")
			setMessages(prev => {
				const index = prev.findIndex(m => m.id === aiMsg.id)
				if (index === -1) return prev

				const updatedMessages = [...prev]
				const oldMsg = updatedMessages[index]

				updatedMessages[index] = {
					...oldMsg,
					streaming: true,
					body: oldMsg.body + data
				}

				return updatedMessages
			})
		}

		const onComplete = (data) => {
			if (isStale()) return
			// do we need to do anything?
			/*
			 * NOTE: an older comment here claimed the list would be refreshed by a realtime INSERT
			 * event once the server saved the message. There is no such subscription in this
			 * component -- every setMessages call is local -- so this state is only ever what the
			 * client put in it. Believing otherwise is part of how 098 survived.
			 */
			log.debug("streaming onComplete, calling setMessages " + data)

			log.debug("setMessages:: streaming onComplete")
			setMessages(prev => {
				const index = prev.findIndex(m => m.id === aiMsg.id)
				if (index === -1) return prev

				const updatedMessages = [...prev]
				const oldMsg = updatedMessages[index]

				updatedMessages[index] = {
					...oldMsg,
					body: data,
					streaming: false
				}

				return updatedMessages
			})
		}

		const sendUrl = `/mini/prepkit/${prepKit.id}/question/${question.id}/send`
			+ (answeredLetter ? `?answered=${encodeURIComponent(answeredLetter)}` : '')
		doStreamPost(sendUrl, {body: JSON.stringify(serverMsgs)}, handleSendResponse, onData, onComplete)
			.catch(err => {
				log.debug("Error sending message")
				log.debug(err)

				/*
				 * The placeholder bubble is already on screen and spinning, so the failure has to
				 * land in it -- otherwise it spins forever. The server's own text is shown when it
				 * sent one (089 answers 503 with a line written for the student); `streaming:false`
				 * stops the cursor, and nothing is saved, so a reload clears it.
				 */
				if (isStale()) return
				setMessages(prev => {
					const index = prev.findIndex(m => m.id === aiMsg.id)
					if (index === -1) return prev

					const updatedMessages = [...prev]
					updatedMessages[index] = {
						...updatedMessages[index],
						body: err.message || "Something went wrong. Please try again in a moment.",
						streaming: false,
						error: true,
					}
					return updatedMessages
				})
			})
	}

	/*
	 * Mobile Quick-Start buttons (desktop has the same handler inside AskBirdsyPanel):
	 * "Show correct answer" reveals the option instead of sending, everything else sends with
	 * its `type` so the action is hidden for the rest of this question.
	 */
	const handleQuickStart = (a) => {
		handleSend({displayText: a.displayText, type: a.type})
	}

	const handleAnswer = async (q, a, label) => {
		const isCorrect = question.answer === a

		/*
		 * A wrong answer reveals the right one. It used to stay hidden until the user asked, which
		 * left them looking at a red option with no idea which one was correct -- and the chat reply
		 * is told not to give it away, so nothing on screen answered the obvious question.
		 */
		if (!isCorrect) setRevealCorrect(true)

		const answer = {
			question: q,
			answer: a,
			is_correct: isCorrect,
			created_at: new Date()
		}

		recordAnswer(q, answer)

		// the answer replaces whatever Change Answer unlocked, so the question locks again
		setUnlockedQuestionIds(prev => prev.filter(id => id !== q.id))

		// the letter travels with the send so the server knows this was a click, not typed (082)
		handleSend(`${abcd(a)}. ${label}`, abcd(a))
		await saveAnswer(a)
	}

	/*
	 * Change Answer (101). Offered on an incorrect answer only — nobody wants to change a correct
	 * one, and offering it there would just invite second-guessing.
	 *
	 * Purely local, and deliberately so. There is no "reopen the session" call: the answer route
	 * already clears completed_at before it writes, so the replacement answer lands inside the
	 * session's window on its own. Reopening here as a separate step would leave a session marked
	 * incomplete if the student unlocked an answer and then wandered off without replacing it —
	 * a topic showing RESUME with every question answered and locked, and no way back to complete.
	 *
	 * `answerToShow` is null while a question is unlocked, so the options unlock and the old answer
	 * stops being highlighted without anything being forgotten: navigating away and back restores
	 * it, and the record is untouched until a replacement is actually submitted.
	 */
	const handleChangeAnswer = () => {
		if (!question) return

		setUnlockedQuestionIds(prev => prev.includes(question.id) ? prev : [...prev, question.id])
		setRevealCorrect(false)

		// the summary has to be re-earned by actually answering again
		justCompletedTopicRef.current = null
	}

	if (!question) return null


	/*
	 * The three-up Back / PrepKit Dashboard / Continue bar that used to live here is gone (078).
	 * Its two consumers — the topic title and topic score screens — render StudyScreenActions
	 * from the handlers below instead.
	 */

	return (
		<Box
			{...handlers}
			sx={{
				border: `0px solid blue`,
				/*
				 * Desktop is a two-pane app layout filling the whole window — study mode drops the
				 * Navbar (see AppLayout), so there is no offset to cancel here. Each column scrolls
				 * on its own and the page itself never scrolls. Small screens keep page scroll.
				 */
				...(isSmallScreen ? {} : {width: '100%', height: '100vh', overflow: 'hidden'}),
			}}
			display={"flex"}
			flexDirection={"column"}
		>
			<Helmet>
				{
					showTopic
						? <title>{prepKit && `PrepKit Topic - ${topic && topic.name} - ${prepKit.name} - Birdsy`}</title>
						: <title>{prepKit && `PrepKit Question - ${prepKit.name} - Birdsy`}</title>
				}
			</Helmet>

			{
				isSmallScreen && <Divider/>
			}
			{/* the topic back-link moved into the left column's nav row as "← Dashboard" (044) */}

			{/*
			  * Desktop is full-bleed: the panes together fill the window at the mockup's 7/5 ratio
			  * (A_Questions_v2 uses col-span-7 / col-span-5), so the question column is wider than
			  * the chat column. Below md it is a single fluid column.
			  */}
			<Container
				maxWidth={false}
				disableGutters
				style={{border: "0px solid red"}}
				sx={{
					width: '100%',
					border: `${bw}px solid yellow`,
					// fill the height under the navbar so the columns can be fixed-height
					...(isSmallScreen ? {} : {flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}),
				}}
			>
				{/* A_Questions_v2 layout: question on the left, Ask Birdsy on the right (desktop) */}
				{/* no spacing at all — a gutter or Grid's margins would break the full-bleed seam */}
				<Grid container
				      rowSpacing={0}
				      columnSpacing={0}
				      alignItems={isSmallScreen ? "flex-start" : "stretch"}
				      sx={isSmallScreen ? undefined : {height: '100%', minHeight: 0}}
				>
				<Grid xs={12} md={7}
				      sx={{
						  minWidth: 0,
						  border: `${bw}px solid red`,
						  // question column (7 of 12): white in light mode
						  ...(isSmallScreen ? {} : {
							  height: '100%',
							  minHeight: 0,
							  display: 'flex',
							  flexDirection: 'column',
							  background: theme.custom.bg.surface,
							  /*
							   * Clears the top of the window — study mode has no Navbar, so nothing
							   * else pushes this down and the "← Dashboard" link would otherwise sit
							   * level with AppLayout's fixed brand mark and hamburger.
							   *
							   * 6 (48px), up from 4 (32px). MOVES WITH the Ask Birdsy pane's pt: it
							   * is set two units lower there because that panel pads itself inside,
							   * so changing one without the other unlevels the two columns.
							   */
							  pt: 6,
						  }),
					  }}
				>
					<Grid xs={12}
					      sx={{
							  minWidth: 0,
							  border: `${bw}px solid magenta`,
							  /*
							   * The half's background is full-bleed, but its content is capped and
							   * pushed to the seam (ml auto) so both sides together read as one
							   * centred column. Caps are half of the pre-046 container widths.
							   */
							  ...(isSmallScreen ? {} : {
								  flex: 1,
								  minHeight: 0,
								  display: 'flex',
								  flexDirection: 'column',
								  width: '100%',
								  maxWidth: `min(92%, ${QUESTION_CONTENT_MAX}px)`,
								  ml: 'auto',
							  }),
						  }}
					>
						{/* question + options scroll; the nav row below stays pinned to the column */}
						<Box ref={questionBlockRef}
						     sx={isSmallScreen ? undefined : {flex: 1, minHeight: 0, overflowY: 'auto'}}>
						{/* the link names where it goes; the topic name moved below the card (087) */}
						<Box
							onClick={() => navigate(backDestination.path)}
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 0.5,
								alignSelf: 'flex-start',
								cursor: 'pointer',
								fontSize: theme.custom.fontSize.xs,
								fontWeight: theme.custom.fontWeight.semibold,
								letterSpacing: '0.08em',
								textTransform: 'uppercase',
							color: chromeText,
								// same padding as the question/options block so it shares their left edge
								px: isSmallScreen ? 2 : 4,
								pt: 1,
								transition: 'color 150ms',
								// the arrow follows the label on hover, like the nav row's icon buttons
								'& .MuiSvgIcon-root': {color: theme.custom.text.main, transition: 'color 150ms'},
								'&:hover': {color: theme.custom.link},
								'&:hover .MuiSvgIcon-root': {color: theme.custom.link},
							}}
						>
							{/* lineHeight 1 collapses the text box to its cap height so the two centre together */}
							<ArrowBack sx={{fontSize: 16}}/>
							<Box component="span" sx={{lineHeight: 1}}>{backDestination.label}</Box>
						</Box>
						<Question prepKit={prepKit}
						              answerToShow={answerToShow}
						              question={question}
						              topic={topic}
						              parentTopics={parentTopics}
						              revealCorrect={revealCorrect}
						              onSend={handleSend}
						              onAnswer={handleAnswer}
						              onChangeAnswer={handleChangeAnswer}
						/>
						{/*
						  * Topic and counter share one row beneath the card (087): topic left,
						  * counter right. Baseline alignment, not centre, so the two sit on one
						  * line despite the different weights. Same horizontal padding as the
						  * question/options block so both line up with the card's edges.
						  */}
						<Box sx={{
							px: isSmallScreen ? 2 : 4,
							py: 1,
							display: 'flex',
							alignItems: 'baseline',
							justifyContent: 'space-between',
							gap: 2,
						}}>
							{/* same type as the counter opposite it — only the alignment differs */}
							<Typography sx={{
								fontSize: "13px",
								color: chromeText,
								minWidth: 0,
								overflowWrap: 'anywhere',
							}}>
								{topic.name}
							</Typography>
							<Typography sx={{fontSize: "13px", color: chromeText, whiteSpace: 'nowrap'}}>
								Question&nbsp;{question.number}&nbsp;of&nbsp;{topic.questions.length}
							</Typography>
						</Box>
						</Box>

						{/* desktop nav row — pinned to the bottom of the left column */}
						{
							!isSmallScreen &&
							<Box sx={{
								display: 'flex',
								alignItems: 'center',
								// Dashboard moved to the back link at the top of the column (088),
								// so Previous/Next are alone here and stay at the seam
								justifyContent: 'flex-end',
								gap: 2,
								flexShrink: 0,
								// same 64px band as the Ask Birdsy input so the two bars line up
								minHeight: 64,
								py: 0,
								pl: 1,
								// Next/Skip sits at the seam, so it needs room on its right
								pr: 4,
							}}>
								<Box display="flex" alignItems="center" gap={1}>
								<Button
									onClick={previousQuestion}
									startIcon={<KeyboardArrowLeft sx={{fontSize: 18}}/>}
									sx={{
										textTransform: 'none',
										fontSize: theme.custom.fontSize.md,
										color: chromeText,
										'&:hover': {color: theme.custom.link, background: 'transparent'},
									}}
								>
									Previous
								</Button>
								<Button
									onClick={handleNext}
									endIcon={
										answered
											? <KeyboardArrowRight sx={{fontSize: 18}}/>
											: <KeyboardDoubleArrowRight sx={{fontSize: 18}}/>
									}
									sx={{
										// the mockup's pulse, only once there is an answer to move on from
										animation: answered ? `${softPulse} 1.6s ease-in-out infinite` : 'none',
									textTransform: 'none',
										fontSize: '15px',
										fontWeight: theme.custom.fontWeight.semibold,
										borderRadius: `${theme.custom.radius.sm}px`,
										px: 3, py: 1.25,
										// 44px keeps it a comfortable target on touch screens
										minHeight: 44,
										background: answered ? theme.custom.indigo.primary : 'transparent',
									color: answered ? '#fff' : chromeText,
									// border.strong is only a few percent off the dark pane — lift the outline there
									border: answered
											? 'none'
											: `1px solid ${theme.palette.mode === 'dark' ? theme.custom.chip.border : theme.custom.border.strong}`,
										'&:hover': {
											background: answered
												? theme.custom.indigo.hover
												: theme.custom.bg.dropdownHover
										},
									}}
								>
									{answered ? 'Next' : 'Skip'}
								</Button>
								</Box>
							</Box>
						}

					</Grid>
				{/* the mobile pinned-question duplicate was removed in 050 — mobile is one scroll
				    column and navigation lives in the fixed QuestionInput bar */}
				</Grid>
				<Grid xs={12} md={5} sx={{
					minWidth: 0,
					mt: isSmallScreen ? 1 : 0,
					// clear the fixed bottom bar (input + nav only — the actions scroll with the chat)
					pb: isSmallScreen ? 'calc(70px + env(safe-area-inset-bottom))' : 0,
					mr: isSmallScreen ? 1 : 0,
					border: `0px solid red`,
					// chat column (5 of 12): light gray in light mode
					...(isSmallScreen ? {} : {
						height: '100%',
						minHeight: 0,
						display: 'flex',
						flexDirection: 'column',
						/*
						 * Flat gray, plus a short gradient at the seam so the left pane reads as
						 * casting a shadow onto this one. Painted here rather than as a box-shadow
						 * on the left pane so it cannot overlap the question content.
						 */
						background: theme.palette.mode === 'dark'
							? `linear-gradient(to right, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0) 8px), ${theme.custom.bg.cardInner}`
							: `linear-gradient(to right, rgba(15, 23, 42, 0.12), rgba(15, 23, 42, 0) 8px), ${theme.custom.bg.cardInner}`,
						// the two panes are only a few percent apart in dark — give the seam an edge
						borderLeft: `1px solid ${theme.custom.border.color}`,
						// matches the question column's top inset (the panel pads itself inside), so this
						// stays exactly two units below it whenever that one moves
						pt: 4,
					}),
				}}>
					{
						/*
						 * The panel spans the whole pane so its scroll bar sits at the window edge;
						 * it caps its own rows internally and pushes them to the seam, mirroring
						 * the question column.
						 */
						!isSmallScreen &&
						<AskBirdsyPanel
							contextId={user.id}
							messages={showTopic ? [] : filteredMessages}
							actions={questionActions}
							messagesEndRef={messagesEndRef}
							onSend={handleSend}
							onShowCorrect={() => setRevealCorrect(true)}
							contentMaxWidth={`min(92%, ${CHAT_CONTENT_MAX}px)`}
						/>
					}
					{
					//<pre>{JSON.stringify({hasMessages: question.hasMessages})}</pre>
					//<pre>{JSON.stringify({messages: messages && messages.length})}</pre>
					//<pre>{JSON.stringify({currentAnswer})}</pre>
					//<pre>{JSON.stringify({topic: topic.percentAnswered})}</pre>
					//<pre>{JSON.stringify({loadedAt})}</pre>
					}

					{
						isSmallScreen &&
						!showTopic &&
						<Messages
							contextId={user.id}
							messages={filteredMessages}
							showMessagesAfter={0}
						/>
					}
					{
						/* actions flow with the chat so they land where the user is reading */
						isSmallScreen &&
						!showTopic &&
						<Box sx={{px: 2, pt: 2}}>
							<QuickStartActions
								actions={questionActions}
								onAction={handleQuickStart}
								isStreaming={hasStreamingMessage(filteredMessages)}
							/>
						</Box>
					}
					{
						/* the desktop "no messages yet" action row is now the Ask Birdsy panel above */
					}
					{
						// currentAnswer &&
						// <Box display="flex" justifyContent="center">
						// 	<Chip
						// 		label="Next"
						// 		color="info"
						// 		variant="outlined"
						// 		onClick={nextQuestion}
						// 		onDelete={nextQuestion}
						// 		deleteIcon={<ArrowForward/>}
						// 		sx={{
						// 			cursor: "pointer",
						// 			marginLeft: '5px',
						// 			marginRight: '5px',
						// 			padding: '0',
						// 			fontSize: '.9em',
						// 			'&.MuiChip-root': {
						// 				xpadding: '26px',
						// 				xborderRadius: '26px'
						// 			}
						// 		}}
						// 	/>
						// </Box>
					}
				</Grid>
				</Grid>
			</Container>

			{/* mini question input — small screens only; desktop asks from the Ask Birdsy panel */}
			{
				isSmallScreen &&
				!showTopic &&
				!showTopicScore &&
				<QuestionInput
					onSend={handleSend}
					onDashboard={() => navigate(`/prepkit/${prepKit && prepKit.id}`)}
					messageInputDisabled={showTopic}
					currentAnswer={answerToShow}
					answered={answered}
					onBack={handleBack}
					onNext={handleNext}
				/>
			}
			{/* back to the question once it has scrolled away (mobile only) */}
			<Zoom in={isSmallScreen && !showTopic && !questionInView}>
				<Fab
					size="small"
					aria-label="Back to the question"
					onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
					sx={{
						position: 'fixed',
						right: 16,
						bottom: 'calc(84px + env(safe-area-inset-bottom))',
						zIndex: 1250,
						background: theme.custom.bg.surface,
						color: theme.custom.text.main,
						border: `1px solid ${theme.custom.border.color}`,
						'&:hover': {background: theme.custom.bg.dropdownHover},
					}}
				>
					<KeyboardArrowUp/>
				</Fab>
			</Zoom>
			{
				isSmallScreen &&
				<div style={{marginBottom: "20px", border: "0px solid red"}} ref={messagesEndRef}/>
			}

			{
				showTopic &&
				<Box
					sx={{
						position: 'absolute',
						/*
						 * Both interstitials cover the whole window (078). The 70px band this used
						 * to leave open is the topic back-link / Ask Birdsy header; seeing it behind
						 * a full-screen title or summary made them read as panels floating over the
						 * question rather than as a start and a finish line.
						 */
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: theme.palette.background.paper,
						border: `${bw}px solid pink`,
						zIndex: 1100,
						// actions are inline on both screens now, so there is no fixed bar to clear
						pb: 0,
						overflowY: 'auto',
						display: 'flex',              // Add this
						flexDirection: 'column',      // Add this
						justifyContent: 'flex-start',     // Add this if you want vertical centering
					}}
				>
					{/*
					  * The score screen renders its own two actions inline (077) rather than the
					  * shared three-up continueButtons bar: from here it is either on to the next
					  * section/topic or back to the dashboard. TopicTitle below still uses the bar.
					  */}
					{
						/*
						 * topicScoreTopic, not just showTopicScore: TopicScore reads topic.name, so
						 * it must not render on a frame where the id is set but the topic has not
						 * been resolved out of prepKit (or has gone from the kit entirely).
						 */
						showTopicScore &&
						topicScoreTopic &&
						<TopicScore
							topic={topicScoreTopic}
							aiMessage={topicScoreMessage}
							onContinue={handleNext}
							onDashboard={() => navigate(`/prepkit/${prepKit.id}`)}
						/>
					}
					{
						showTopic &&
						!showTopicScore &&
						<TopicTitle
							topic={topic}
							parentTopics={parentTopics}
							onContinue={handleNext}
							onDashboard={() => navigate(`/prepkit/${prepKit.id}`)}
						/>
					}
				</Box>
			}
			{/*
			<Tour token={"question"} last={"Let's Go!"}/>
			*/}
		</Box>
	)
}

export default React.memo(QuestionView)